import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4 rounded-[1.2rem] border border-[#d8e4f0] bg-white p-4 sm:p-5 shadow-[0_4px_16px_rgba(15,23,42,0.03)] transition hover:border-[#bfd0e6] lg:flex-row lg:items-center lg:justify-between" }
const _hoisted_2 = { class: "flex items-start sm:items-center gap-3.5 min-w-0" }
const _hoisted_3 = { class: "min-w-0" }
const _hoisted_4 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_5 = { class: "text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]" }
const _hoisted_6 = {
  key: 0,
  class: "inline-flex rounded-full bg-[#eaf3fb] px-2 py-0.5 text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]"
}
const _hoisted_7 = { class: "mt-0.5 text-[0.98rem] font-extrabold text-[#0f172a] truncate" }
const _hoisted_8 = { class: "mt-0.5 text-[0.85rem] text-[#64748b] truncate" }
const _hoisted_9 = { class: "flex flex-wrap items-center gap-3 justify-between lg:justify-end" }
const _hoisted_10 = { class: "flex flex-wrap items-center gap-2" }

import { computed } from "vue";
import {
  Eye,
  FilePenLine,
  FileText,
  FileUp,
  Link,
  Plus,
  Trash2,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import type { ReportMonthSlot, ReportStatusMeta } from "@/types/reports";


export default /*@__PURE__*/_defineComponent({
  __name: 'MonthlyReportCard',
  props: {
    monthSlot: {},
    canGenerate: { type: Boolean },
    canView: { type: Boolean },
    canDownload: { type: Boolean },
    canDelete: { type: Boolean },
    canUpload: { type: Boolean },
    canUpdate: { type: Boolean },
    showHtmlActions: { type: Boolean },
    emptyMessage: {},
    statusMeta: {},
    isCustom: { type: Boolean }
  },
  emits: ["generate", "view", "download-pdf", "download-html", "upload", "update-report", "delete-report"],
  setup(__props: any) {

const toast = useToast();

const props = __props;



const isReady = computed(() => {
  const status = String(props.monthSlot.report?.status || props.monthSlot.status).toLowerCase();
  return status === "generated" || status === "published";
});

const isDraft = computed(
  () => String(props.monthSlot.report?.status || props.monthSlot.status).toLowerCase() === "draft"
);

const isUploaded = computed(() => props.monthSlot.report?.source_type === "uploaded");

const isCustomSlot = computed(() => {
  return props.isCustom || props.monthSlot.month_number > 3;
});

const statusBadgeClasses = computed(() => {
  const tone = props.statusMeta?.tone;
  if (tone === "green" || isReady.value) return "bg-[#e6faf4] text-[#0d8b68]";
  if (tone === "amber" || isDraft.value) return "bg-[#fff6d8] text-[#8b6b00]";
  if (tone === "rose") return "bg-[#ffe9ec] text-[#be243d]";
  if (tone === "blue") return "bg-[#eaf3fb] text-[#2f6fab]";
  return "bg-[#ffe9ec] text-[#be243d]";
});

const formattedDate = computed(() => {
  const dateStr =
    props.monthSlot.report?.generated_at ||
    props.monthSlot.report?.created_at ||
    props.monthSlot.last_generated_at;
  if (!dateStr) return "";

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
});

const secondaryMetaText = computed(() => {
  if (props.monthSlot.report) {
    const actionLabel = isDraft.value
      ? "Last saved"
      : isUploaded.value
      ? props.monthSlot.report?.replaced_at
        ? "Updated"
        : "Uploaded"
      : "Last generated";
    return formattedDate.value ? `${actionLabel} ${formattedDate.value}` : "Report available";
  }
  return "No report has been generated for this month yet.";
});

async function handleCopyShareLink() {
  const shareUrl = props.monthSlot.report?.share_url;
  if (!shareUrl) {
    toast.error("No share link available for this report.");
    return;
  }

  let copied = false;
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    }
  } catch {
    copied = false;
  }

  if (!copied) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      copied = document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      copied = false;
    }
  }

  if (copied) {
    toast.success("Share link copied to clipboard!");
  } else {
    prompt("Copy share link:", shareUrl);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass(["flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.9rem] transition", isReady.value ? 'bg-[#eaf3fb] text-[#2f6fab]' : 'bg-[#fff7ed] text-[#f97316]'])
      }, [
        _createVNode(_unref(FileText), { class: "h-5 w-5" })
      ], 2),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("span", _hoisted_5, " Month " + _toDisplayString(__props.monthSlot.month_number), 1),
          (isCustomSlot.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, " Custom "))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("h3", _hoisted_7, _toDisplayString(__props.monthSlot.report_month_label), 1),
        _createElementVNode("p", _hoisted_8, _toDisplayString(secondaryMetaText.value), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("span", {
        class: _normalizeClass(["inline-flex rounded-full px-2.5 py-1 text-[0.68rem] font-extrabold uppercase tracking-[0.08em]", statusBadgeClasses.value])
      }, _toDisplayString(__props.statusMeta.label), 3),
      _createElementVNode("div", _hoisted_10, [
        (isReady.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (__props.canView)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#173b5f] bg-[#173b5f] px-3.5 py-2 text-[0.88rem] font-extrabold text-white transition hover:-translate-y-0.5",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('view')))
                  }, [
                    _createVNode(_unref(Eye), { class: "h-4 w-4" }),
                    _cache[6] || (_cache[6] = _createTextVNode(" View Report ", -1))
                  ]))
                : _createCommentVNode("", true),
              (__props.canView && __props.monthSlot.report?.share_url)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#bbf7d0] bg-[#f0fdf4] px-3.5 py-2 text-[0.88rem] font-extrabold text-[#15803d] transition hover:-translate-y-0.5 hover:bg-[#dcfce7]",
                    title: "Copy Share Link",
                    onClick: handleCopyShareLink
                  }, [
                    _createVNode(_unref(Link), { class: "h-4 w-4" }),
                    _cache[7] || (_cache[7] = _createTextVNode(" Share ", -1))
                  ]))
                : _createCommentVNode("", true),
              (__props.canUpdate)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#d7e2ef] bg-white px-3.5 py-2 text-[0.88rem] font-extrabold text-[#64748b] transition hover:-translate-y-0.5",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update-report')))
                  }, [
                    _createVNode(_unref(FilePenLine), { class: "h-4 w-4" }),
                    _cache[8] || (_cache[8] = _createTextVNode(" Update Report ", -1))
                  ]))
                : _createCommentVNode("", true),
              (__props.canDelete)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 3,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#fee2e2] bg-[#fff5f5] px-3 py-2 text-[0.88rem] font-extrabold text-[#dc2626] transition hover:-translate-y-0.5 hover:bg-[#fee2e2]",
                    title: "Delete Report",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('delete-report')))
                  }, [
                    _createVNode(_unref(Trash2), { class: "h-4 w-4" }),
                    _cache[9] || (_cache[9] = _createTextVNode(" Delete ", -1))
                  ]))
                : _createCommentVNode("", true)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (__props.canGenerate)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#173b5f] bg-[#173b5f] px-3.5 py-2 text-[0.88rem] font-extrabold text-white transition hover:-translate-y-0.5",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('generate')))
                  }, [
                    _createVNode(_unref(Plus), { class: "h-4 w-4" }),
                    _createTextVNode(" " + _toDisplayString(isDraft.value ? "Continue Draft" : `Generate Month ${__props.monthSlot.month_number} Report`), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.canUpload)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#d7e2ef] bg-white px-3.5 py-2 text-[0.88rem] font-extrabold text-[#64748b] transition hover:-translate-y-0.5",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('upload')))
                  }, [
                    _createVNode(_unref(FileUp), { class: "h-4 w-4" }),
                    _cache[10] || (_cache[10] = _createTextVNode(" Upload Report ", -1))
                  ]))
                : _createCommentVNode("", true),
              (isDraft.value && __props.canDelete)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    type: "button",
                    class: "inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#fee2e2] bg-[#fff5f5] px-3 py-2 text-[0.88rem] font-extrabold text-[#dc2626] transition hover:-translate-y-0.5 hover:bg-[#fee2e2]",
                    title: "Delete Draft",
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('delete-report')))
                  }, [
                    _createVNode(_unref(Trash2), { class: "h-4 w-4" }),
                    _cache[11] || (_cache[11] = _createTextVNode(" Delete ", -1))
                  ]))
                : _createCommentVNode("", true)
            ], 64))
      ])
    ])
  ]))
}
}

})